import { SBaseDisplay, SDate } from '@summit/core';
import {
  DaysOfWeek,
  DaysOfWeekToString,
  MileageFormatter,
  MoneyFormatter,
  PercentageFormatter,
  RateFormatter,
  SummitFormattedText
} from '@summit/models';

import { HourlyWorkType } from '@app/models/ebo/digital-logs/hourly-work-type.enum';
import { Dayjs } from 'dayjs';
import { EboObjectTimePeriod } from '../../base-classes/ebo-object-time-period';
import { Employee } from '../../employee/employee';
import { EmployeePayrollGroup } from '../../employee/employee-payroll-group';
import { EmployeeStatus } from '../../employee/status/employee-status.enum';
import { Company } from '../../organization/company/company';
import { PayUnit } from '../../payroll/pay-unit.enum';
import {
  GetApplicableEmployee,
  GetDefaultSortString,
  IWageRuleOrdered
} from '../../payroll/shared/i-wage-rule-ordered';
import { PayType, PayTypeMapping } from '../../payroll/shared/pay-type.enum';
import { Hub } from '../../places/hub/hub';
import { RouteLegWageType } from '../../route/route-leg-wage-type.enum';
import { DigitalLogType } from '../../shared/digital-log-type.enum';
import { WageEmployeeFilterType } from '../../shared/payroll/wage-employee-filter-type.enum';
import { RouteLegWageBase } from '../../shared/route-leg-wage-base';
import { Division } from '../division';
import { DivisionLegWageSet } from './division-leg-wage-set';

//Stupid compiler can't handle this as a base class
export class DivisionLegWage extends EboObjectTimePeriod implements SBaseDisplay, IWageRuleOrdered {
  divisionID: string;
  division: Division;

  ruleOrder: number;
  //Rule Set Instead
  ruleSetId: string;
  ruleSet: DivisionLegWageSet;

  //Filters
  employeeFilterType: WageEmployeeFilterType;
  payrollGroupId: string;
  payrollGroup: EmployeePayrollGroup;
  employeeId: string;
  employee: Employee;
  companyId: string;
  company: Company;
  employeeStatus: EmployeeStatus;
  days: DaysOfWeek;
  invertFilter: boolean;

  //Log Filter
  logType: DigitalLogType;
  originHubID: number;
  originHub: Hub;
  payType: PayType;
  //Linehaul
  wageType: RouteLegWageType;
  distance: number;
  destHubID: number;
  destHub: Hub;
  finalHubId: number;
  finalHub: Hub;
  directOnly: boolean; //unused?
  //Hourly
  hourlyWorkType: HourlyWorkType;

  //Pay Information
  incrementalToWage: boolean;
  blockEmployeeWage: boolean;
  blockEmployeeLegPay: boolean;
  amount: number;
  percentageOfGross: number;
  units: PayUnit;
  mileageAdjustment: number;
  comment: string;

  //tracking fields
  createdDate: Dayjs;

  constructor(jsonData?: Partial<DivisionLegWage>) {
    super(jsonData);
    if (jsonData) {
      if (jsonData.division) this.division = new Division(jsonData.division);
    }
    if (jsonData) {
      if (jsonData.ruleSet) this.ruleSet = new DivisionLegWageSet(jsonData.ruleSet);
      if (jsonData.originHub) this.originHub = new Hub(jsonData.originHub);
      if (jsonData.destHub) this.destHub = new Hub(jsonData.destHub);
      if (jsonData.finalHub) this.finalHub = new Hub(jsonData.finalHub);
      if (jsonData.employee) this.employee = new Employee(jsonData.employee);
      if (jsonData.company) this.company = new Company(jsonData.company);
      if (jsonData.payrollGroup) this.payrollGroup = new EmployeePayrollGroup(jsonData.payrollGroup);
      if (jsonData.createdDate) this.createdDate = SDate.newUtc(jsonData.createdDate);
    }
    this.directOnly ??= true;
    this.incrementalToWage ??= false;
    this.units ??= PayUnit.Dollars;
    this.payType ??= PayType.Solo;
    this.wageType ??= RouteLegWageType.FromTo;
    this.days ??= DaysOfWeek.AllDays;
    this.blockEmployeeWage ??= false;
    this.employeeFilterType ??= WageEmployeeFilterType.All;
    this.logType ??= DigitalLogType.FedExLinehaul;
  }

  //dup from route-leg-wage-base
  static sort(n1: RouteLegWageBase, n2: RouteLegWageBase) {
    //console.log(`comparing ${n1.defaultSort} to ${n2.defaultSort}`);
    return n1.defaultSort > n2.defaultSort ? 1 : -1;
  }

  get applicableEmployee(): SummitFormattedText {
    return GetApplicableEmployee(this);
  }

  get amountSummary(): SummitFormattedText {
    if (this.ruleSetId) return null;
    const response = new SummitFormattedText();
    if (this.incrementalToWage) response.AddLine('Additional');
    if (this.units == PayUnit.Dollars) response.AddLine(MoneyFormatter(this.amount));
    if (this.units == PayUnit.Miles) response.AddLine(`${MileageFormatter(this.amount)} Miles`);
    if (this.units == PayUnit.MileageRate) response.AddLine(`${RateFormatter(this.amount)} / Mile`);
    if ((this.percentageOfGross ?? 0) != 0)
      response.AddLine(`${PercentageFormatter(this.percentageOfGross / 100)} of Gross`);
    if (this.blockEmployeeWage === true) response.AddLine(`[Flat]`);
    if (this.blockEmployeeLegPay === true) response.AddLine(`[Blocks Leg Wage]`);

    return response.lines.length > 0 ? response : null;
  }

  get dayString(): string {
    if (this.ruleSet) return null;
    return DaysOfWeekToString(this.days);
  }
  get defaultSort() {
    return GetDefaultSortString(this);
  }

  toShortString(_param?: string): string {
    if (this.ruleSetId?.length > 0) return `[Rule Set] ${this.ruleSet?.name}`;
    let response = this.incrementalToWage ? 'Incremental ' : '';
    response += PayTypeMapping[this.payType] + ' ';
    switch (this.wageType) {
      case RouteLegWageType.ShuttleDistance:
        response += `${this.originHub?.toShortString()}<=>? for ${this.distance} Miles = ${MoneyFormatter(
          this.amount
        )}`;
        break;
      case RouteLegWageType.FromTo:
        response += `${this.originHub?.toShortString()}=>${this.destHub?.toShortString()} = ${MoneyFormatter(
          this.amount
        )}`;
        break;
      case RouteLegWageType.ShuttleDestination:
        if (this.directOnly)
          response += `${this.originHub?.toShortString()}<=>${this.destHub?.toShortString()} = ${MoneyFormatter(
            this.amount
          )}`;
        else
          response += `${this.originHub?.toShortString()}=>?=>${this.destHub?.toShortString()} = ${MoneyFormatter(
            this.amount
          )}`;
        break;
      case RouteLegWageType.MilesLessEqual:
        response += `Distance <= ${this.distance} Miles = ${MoneyFormatter(this.amount)}`;
        break;
      default:
        break;
    }
    if (this.percentageOfGross) response += ` + ${PercentageFormatter(this.percentageOfGross / 100)} of Gross`;
    if (response.length == 0) return 'Unknown';
    return response;
  }

  HasDay(flag: DaysOfWeek): boolean {
    // eslint-disable-next-line no-bitwise
    return flag === (this.days & flag);
  }

  SetDay(flag: DaysOfWeek, value: boolean) {
    if (value) {
      // eslint-disable-next-line no-bitwise
      this.days |= flag;
    } else {
      // eslint-disable-next-line no-bitwise
      this.days &= ~flag;
    }
  }
}
